import { useCallback, useEffect, useRef, useState } from 'react';

import { getQuestionnaireScore } from '@/apis';
import { QuestionnaireScore, ResultData } from '@/types';

import { useApiHandler } from './useApiHandler';

interface UseQuestionnaireScoreResult {
  scoreData: QuestionnaireScore | null;
  resultData: ResultData[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  exportable: boolean;
  isGenerating: boolean;
}

// SDG標題對照表
const SDG_TITLES = [
  { title: '消除貧窮', subtitle: 'No Poverty' },
  { title: '消除飢餓', subtitle: 'Zero Hunger' },
  { title: '良好健康與福祉', subtitle: 'Good Health and Well-being' },
  { title: '優質教育', subtitle: 'Quality Education' },
  { title: '性別平等', subtitle: 'Gender Equality' },
  { title: '淨水與衛生', subtitle: 'Clean Water and Sanitation' },
  { title: '可負擔的潔淨能源', subtitle: 'Affordable and Clean Energy' },
  { title: '良好工作與經濟成長', subtitle: 'Decent Work and Economic Growth' },
  {
    title: '產業、創新與基礎設施',
    subtitle: 'Industry, Innovation and Infrastructure',
  },
  { title: '減少不平等', subtitle: 'Reduced Inequalities' },
  { title: '永續城市與社區', subtitle: 'Sustainable Cities and Communities' },
  {
    title: '負責任的消費與生產',
    subtitle: 'Responsible Consumption and Production',
  },
  { title: '氣候行動', subtitle: 'Climate Action' },
  { title: '保育海洋生態', subtitle: 'Life Below Water' },
  { title: '保育陸域生態', subtitle: 'Life on Land' },
  {
    title: '和平與正義的制度',
    subtitle: 'Peace, Justice and Strong Institutions',
  },
  { title: '全球夥伴關係', subtitle: 'Partnerships for the Goals' },
];

export const useQuestionnaireScore = (
  questionnaireId?: string,
): UseQuestionnaireScoreResult => {
  const [scoreData, setScoreData] = useState<QuestionnaireScore | null>(null);
  const [resultData, setResultData] = useState<ResultData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportable, setExportable] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { handleApiCall } = useApiHandler();
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchScore = useCallback(async () => {
    if (!questionnaireId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await handleApiCall(() =>
        getQuestionnaireScore(questionnaireId, false),
      );

      if (!response) {
        throw new Error('No response received');
      }

      // Check if feedbacks are ready
      if (response.feedbacksReady === false) {
        setIsGenerating(true);
        setScoreData(null);
        setResultData([]);

        // Start polling if not already polling
        if (!pollingIntervalRef.current) {
          pollingIntervalRef.current = setInterval(() => {
            fetchScore();
          }, 5000); // Poll every 5 seconds
        }
        return;
      }

      // If we successfully got the response with ready feedbacks, stop polling
      setIsGenerating(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      setScoreData(response);
      setExportable(response.exportable || false);

      // 轉換為 ResultData 格式
      const transformedData: ResultData[] = response.sdgScores.map(
        (sdgScore, index) => {
          const sdgTitleInfo = SDG_TITLES[index] || {
            title: `SDG ${index + 1}`,
            subtitle: sdgScore.sdgGoal.name,
          };

          return {
            id: index + 1,
            title: sdgTitleInfo.title,
            subtitle: sdgTitleInfo.subtitle,
            score: sdgScore.score,
            description: sdgScore.aiDescription || '暫無描述資料',
          };
        },
      );

      setResultData(transformedData);
    } catch (err) {
      console.error('Failed to fetch questionnaire score:', err);
      const errorMessage = err instanceof Error ? err.message : '獲取分數資料失敗';
      setError(errorMessage);
      setIsGenerating(false);

      // Stop polling on error
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  }, [questionnaireId, handleApiCall]);

  useEffect(() => {
    fetchScore();

    // Cleanup: Clear polling interval when component unmounts
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [questionnaireId]);

  return {
    scoreData,
    resultData,
    loading,
    error,
    refetch: fetchScore,
    exportable,
    isGenerating,
  };
};
